/**
 * Lead API Service
 */

import { apiClient } from './client'
import type {
  DuplicateCheckResponse,
  LeadCreate,
  LeadUpdate,
  LeadResponse,
  LeadListResponse,
  LeadFilterParams,
  LeadTransition,
} from '@/types/api'

/**
 * Get paginated list of leads with optional filters
 */
export async function getLeads(params?: LeadFilterParams): Promise<LeadListResponse> {
  return apiClient.get<LeadListResponse>('/leads', { params })
}

/**
 * Get a single lead by ID
 */
export async function getLead(id: string): Promise<LeadResponse> {
  return apiClient.get<LeadResponse>(`/leads/${id}`)
}

/**
 * Create a new lead
 */
export async function createLead(data: LeadCreate): Promise<LeadResponse> {
  return apiClient.post<LeadResponse>('/leads', data)
}

/**
 * Update an existing lead
 */
export async function updateLead(id: string, data: LeadUpdate): Promise<LeadResponse> {
  return apiClient.put<LeadResponse>(`/leads/${id}`, data)
}

/**
 * Delete a lead (admin only)
 */
export async function deleteLead(id: string): Promise<void> {
  return apiClient.delete(`/leads/${id}`)
}

/**
 * Transition lead to a new status
 */
export async function transitionLead(id: string, data: LeadTransition): Promise<LeadResponse> {
  return apiClient.post<LeadResponse>(`/leads/${id}/transition`, data)
}

/**
 * Check if a lead with matching identity fields already exists.
 */
export async function checkDuplicate(params: {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
}): Promise<DuplicateCheckResponse> {
  return apiClient.get<DuplicateCheckResponse>('/leads/check-duplicate', { params })
}

export const leadService = {
  getLeads,
  getLead,
  createLead,
  updateLead,
  deleteLead,
  transitionLead,
  checkDuplicate,
}
